import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import {
  useDarkModeCheck,
  useTheme,
} from '@/contexts/RADashboardThemeContext/hooks'
import { RADashboardLayout } from '@/interfaces/raDashboard.interface'
import { Dashboard } from '@/services/report-dashboard/dashboard.service'
import { validateRADashboardLayoutJSON } from '@/utils/ReportDashboardValidation'
import { validateInput, ValidationPresets } from '@/utils/UserInputValidation'
import { Button } from '@mui/material'
import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import DashboardGuiEditor from './DashboardGuiEditor'
import DashboardJsonEditor from './DashboardJsonEditor'
import DashboardTextEditor from './DashboardTextEditor'

const DASHBOARD_TYPE_LABEL: Record<FADashboardType, string> = {
  [FADashboardType.Risk]: 'Risk Dashboard',
  [FADashboardType.Attribution]: 'Attribution Dashboard',
}

interface DashboardEditorProps {
  dashboard: Dashboard
  isAdminMode?: boolean
  onClose: () => void
  onDelete: () => void
  onSaveDashboard: (layout: RADashboardLayout, description: string, isPrivate: boolean) => void
  onSaveAs?: (layout: RADashboardLayout, newName: string, description: string, isPrivate: boolean) => void
  existingDashboardNames?: string[]
}

const DashboardEditor: React.FC<DashboardEditorProps> = (props) => {
  const isDarkMode = useDarkModeCheck()
  const theme = useTheme()

  const [dashboardObject, setDashboardObject] =
    React.useState<RADashboardLayout>(
      props.dashboard.jsonValue as RADashboardLayout,
    )
  const [editorMode, setEditorMode] = React.useState('gui')
  const [showSaveAsDialog, setShowSaveAsDialog] = React.useState(false)
  const [saveAsName, setSaveAsName] = React.useState('')
  const [showOverwriteWarning, setShowOverwriteWarning] = React.useState(false)
  const [overwriteWarningMessage, setOverwriteWarningMessage] = React.useState('')
  const [editedDescription, setEditedDescription] = React.useState(props.dashboard.dashboardDescription)
  const [isEditingDescription, setIsEditingDescription] = React.useState(false)
  const [descriptionDraft, setDescriptionDraft] = React.useState('')
  const [editedIsPrivate, setEditedIsPrivate] = React.useState(props.dashboard.isPrivate)

  // Admin Mode is restricted to Delete/Save As only, regardless of ownership.
  const canEditMetadata = !props.isAdminMode && !props.dashboard.isLocked && props.dashboard.isEditable !== false && props.dashboard.currentUserOwned
  const canDelete = props.isAdminMode || props.dashboard.currentUserOwned

  useEffect(() => {
    setDashboardObject(props.dashboard.jsonValue as RADashboardLayout)
    setEditedDescription(props.dashboard.dashboardDescription)
    setEditedIsPrivate(props.dashboard.isPrivate)
    setIsEditingDescription(false)
  }, [props.dashboard])

  const openSaveAsDialog = () => {
    setSaveAsName(props.dashboard.dashboardName)
    setShowOverwriteWarning(false)
      setOverwriteWarningMessage('')
    setShowSaveAsDialog(true)
  }

  const trySaveAsDashboard = () => {
    const trimmed = saveAsName.trim()
    if (!trimmed) return

    const nameValidation = validateInput(trimmed, {
      ...ValidationPresets.NAME,
      fieldName: 'Dashboard name',
      maxLength: 50,
    })
    if (!nameValidation.isValid) {
      setShowOverwriteWarning(true)
      setOverwriteWarningMessage(nameValidation.error ?? 'Invalid dashboard name.')
      return
    }

    const nameExists =
      props.existingDashboardNames?.some(
        (n) => n.toLowerCase() === trimmed.toLowerCase(),
      ) ?? false

    if (nameExists) {
      setShowOverwriteWarning(true)
      setOverwriteWarningMessage(`A dashboard named "${trimmed}" already exists. Please choose a different name.`)
      return
    }

    const descriptionValidation = validateInput(editedDescription, {
      ...ValidationPresets.TEXT,
      fieldName: 'Description',
    })
    if (!descriptionValidation.isValid) {
      toast.error(descriptionValidation.error)
      return
    }

    const dashboardString = JSON.stringify(dashboardObject)
    const result = validateRADashboardLayoutJSON(dashboardString, props.dashboard.dashboardType)

    if (!result.valid) {
      toast.error(
        'Invalid JSON. Unable to save Dashboard, please edit and try again.',
      )
      return
    }
    if (result.level === 'loose') {
      toast.warn(
        'JSON includes some unsupported values. Dashboard may not fully behave as expected',
      )
    }
    props.onSaveAs?.(dashboardObject!, trimmed, editedDescription, editedIsPrivate)
    toast.success(`Dashboard saved as "${trimmed}"`)
    setShowSaveAsDialog(false)
    setSaveAsName('')
    setShowOverwriteWarning(false)
      setOverwriteWarningMessage('')
  }

  const trySaveDashboard = () => {
    const descriptionValidation = validateInput(editedDescription, {
      ...ValidationPresets.TEXT,
      fieldName: 'Description',
    })
    if (!descriptionValidation.isValid) {
      toast.error(descriptionValidation.error)
      return
    }

    const dashboardString = JSON.stringify(dashboardObject)
    const result = validateRADashboardLayoutJSON(dashboardString, props.dashboard.dashboardType)

    if (!result.valid) {
      toast.error(
        'Invalid JSON. Unable to save Dashboard, please edit and try again.',
      )
    } else {
      if (result.level === 'loose') {
        toast.warn(
          'JSON includes some unsupported values. Dashboard may not fully behave as expected',
        )
      }

      toast.success('Dashboard saved')
      props.onSaveDashboard(dashboardObject!, editedDescription, editedIsPrivate)
    }
  }

  const isEditorModeSelected = (modeKey: string) => {
    return modeKey === editorMode
  }

  const editorContainerStyle = {
    padding: '20px',
    backgroundColor: theme.inputBackground,
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    color: theme.textSecondary,
  }

  const tabAreaStyle = {
    width: 'fit-content',
    borderRadius: '8px',
    backgroundColor: theme.tabBackground,
    marginTop: '16px',
  }

  const selectedStyle = {
    margin: 2,
    borderRadius: '8px',
    backgroundColor: theme.tabSelectedBackground,
    color: theme.textPrimary,
  }

  const deselectedStyle = {
    margin: 2,
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: theme.textPrimary,
  }

  return (
    <div
      data-testid="dashboard-editor"
      style={{
        marginTop: '24px',
        marginBottom: '48px',
        padding: '24px',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: '8px',
        boxShadow: isDarkMode
          ? '0 4px 12px rgba(0, 0, 0, 0.3)'
          : '0 4px 12px rgba(0,0,0,0.1)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: theme.textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {props.dashboard.dashboardDisplayName ??
              props.dashboard.dashboardName}
            {props.dashboard.isDefault && (
              <span
                style={{
                  padding: '2px 8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  borderRadius: '8px',
                }}
              >
                DEFAULT
              </span>
            )}
            {props.dashboard.isLocked && (
              <span
                style={{
                  padding: '2px 8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  backgroundColor: '#ffce2e',
                  color: 'black',
                  borderRadius: '8px',
                }}
              >
                🔒︎ LOCKED
              </span>
            )}
          </h3>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: '14px',
              color: theme.textSecondary,
            }}
          >
            {DASHBOARD_TYPE_LABEL[props.dashboard.dashboardType]}
          </p>
        </div>

        {!props.dashboard.isLocked && (
          <button
            onClick={canDelete ? props.onDelete : undefined}
            disabled={!canDelete}
            style={{
              padding: '6px 12px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: canDelete ? '#d32f2f' : 'grey',
              color: 'white',
              cursor: canDelete ? 'pointer' : 'not-allowed',
            }}
          >
            Delete
          </button>
        )}
      </div>

      <div style={editorContainerStyle}>
        {isEditingDescription ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' }}>
            <textarea
              autoFocus
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setIsEditingDescription(false)
              }}
              rows={3}
              maxLength={255}
              style={{
                flex: 1,
                padding: '6px 10px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: theme.inputBackground,
                color: theme.textPrimary,
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <button
                onClick={() => {
                  setEditedDescription(descriptionDraft)
                  setIsEditingDescription(false)
                }}
                title='Confirm'
                style={{
                  width: '28px',
                  height: '28px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#2e7d32',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✓
              </button>
              <button
                onClick={() => setIsEditingDescription(false)}
                title='Cancel'
                style={{
                  width: '28px',
                  height: '28px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  backgroundColor: theme.inputBackground,
                  color: theme.textSecondary,
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✕
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{editedDescription}</span>
            {canEditMetadata && (
              <button
                onClick={() => {
                  setDescriptionDraft(editedDescription)
                  setIsEditingDescription(true)
                }}
                title='Edit description'
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: theme.textSecondary,
                  fontSize: '14px',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                ✎
              </button>
            )}
          </div>
        )}

        {!props.isAdminMode && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px' }}>
              <div style={{ ...tabAreaStyle, marginTop: 0 }}>
                <Button
                  style={
                    isEditorModeSelected('gui') ? selectedStyle : deselectedStyle
                  }
                  onClick={() => {
                    setEditorMode('gui')
                  }}
                >
                  GUI
                </Button>
                <Button
                  style={
                    isEditorModeSelected('json') ? selectedStyle : deselectedStyle
                  }
                  onClick={() => {
                    setEditorMode('json')
                  }}
                >
                  JSON
                </Button>
                <Button
                  style={
                    isEditorModeSelected('raw') ? selectedStyle : deselectedStyle
                  }
                  onClick={() => {
                    setEditorMode('raw')
                  }}
                >
                  TEXT
                </Button>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: canEditMetadata ? 'pointer' : 'default', fontSize: '14px', color: theme.textSecondary }}>
                <input
                  type='checkbox'
                  checked={!editedIsPrivate}
                  disabled={!canEditMetadata}
                  onChange={(e) => setEditedIsPrivate(!e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: canEditMetadata ? 'pointer' : 'default' }}
                />
                Public
              </label>
              <span style={{ fontSize: '14px', color: theme.textSecondary }}>
                {editedIsPrivate ? 'Only visible to you' : 'Visible to all users'}
              </span>
            </div>

            {editorMode == 'json' ?
              <DashboardJsonEditor
                layout={dashboardObject}
                isLocked={props.dashboard.isLocked}
                setLayout={setDashboardObject}
                dashboardType={props.dashboard.dashboardType}
              />
            :
              editorMode == 'raw' ?
                <DashboardTextEditor
                    layout={dashboardObject}
                    isLocked={props.dashboard.isLocked}
                    setLayout={setDashboardObject}
                    dashboardType={props.dashboard.dashboardType}
                />
              :
                <DashboardGuiEditor
                  layout={dashboardObject}
                  isLocked={props.dashboard.isLocked}
                  setLayout={setDashboardObject}
                  dashboardType={props.dashboard.dashboardType}
                />
            }
          </>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
        }}
      >
        {!props.isAdminMode && (
          <button
            onClick={trySaveDashboard}
            style={{
              padding: '10px 24px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              backgroundColor: (props.dashboard.isLocked || props.dashboard.isEditable === false || isEditingDescription) ? 'grey' : '#1976d2',
              color: 'white',
              cursor: (props.dashboard.isLocked || props.dashboard.isEditable === false || isEditingDescription) ? 'not-allowed' : 'pointer',
            }}
            disabled={props.dashboard.isLocked || props.dashboard.isEditable === false || isEditingDescription}
          >
            Save
          </button>
        )}

        {props.onSaveAs && (
          <button
            onClick={openSaveAsDialog}
            disabled={isEditingDescription}
            style={{
              padding: '10px 24px',
              border: `1px solid ${theme.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              backgroundColor: isEditingDescription ? 'grey' : theme.cardBackground,
              color: isEditingDescription ? 'white' : theme.textPrimary,
              cursor: isEditingDescription ? 'not-allowed' : 'pointer',
            }}
          >
            Save As
          </button>
        )}

        <button
          onClick={props.onClose}
          style={{
            padding: '10px 24px',
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            backgroundColor: theme.cardBackground,
            color: theme.textPrimary,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>

      {/* Save As dialog */}
      {showSaveAsDialog && (
        <div
          onClick={() => setShowSaveAsDialog(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: theme.cardBackground,
              borderRadius: '8px',
              padding: '28px',
              width: '420px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            }}
          >
            <h3
              style={{
                margin: '0 0 16px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: theme.textPrimary,
              }}
            >
              Save Dashboard As
            </h3>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: theme.textSecondary,
              }}
            >
              New Dashboard Name
            </label>
            <input
              autoFocus
              type='text'
              value={saveAsName}
              onChange={(e) => {
                setSaveAsName(e.target.value)
                setShowOverwriteWarning(false)
      setOverwriteWarningMessage('')
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') trySaveAsDashboard()
                if (e.key === 'Escape') setShowSaveAsDialog(false)
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${showOverwriteWarning ? '#d32f2f' : theme.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: theme.inputBackground,
                color: theme.textPrimary,
                boxSizing: 'border-box',
                marginBottom: showOverwriteWarning ? '10px' : '20px',
              }}
            />

            {showOverwriteWarning && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                  padding: '8px 12px',
                  backgroundColor: isDarkMode ? '#3b1a1a' : '#fff3f3',
                  border: '1px solid #d32f2f',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#d32f2f',
                }}
              >
                {overwriteWarningMessage}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end',
              }}
            >
              <button
                onClick={() => setShowSaveAsDialog(false)}
                style={{
                  padding: '8px 20px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  backgroundColor: theme.cardBackground,
                  color: theme.textPrimary,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={trySaveAsDashboard}
                disabled={!saveAsName.trim()}
                style={{
                  padding: '8px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  backgroundColor: saveAsName.trim() ? '#1976d2' : 'grey',
                  color: 'white',
                  cursor: saveAsName.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardEditor
